import { useState, useRef, useEffect } from "react";
import {
  Modal,
  Card,
  CardTitle,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
  Col,
} from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { CardBody, CardImg } from "reactstrap/lib";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation } from "@tanstack/react-query";
import { EditProgramLicense } from "../../actions/superUserMutations";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";
import { resizeImageForUpload } from "../../Utilities/imageResize";

const ModifyProgramLicenseModal = ({
  isOpen,
  toggle,
  licenseId,
  licenseName,
  licenseImage,
  isAplProgram,
  programName,
}) => {
  const intl = useIntl();
  const inputRef = useRef(null);
  const [newLicenseName, setNewLicenseName] = useState("");
  const [newFile, setNewFile] = useState(null);
  const [fileError, setFileError] = useState("");

  const queryClient = useQueryClient();
  const editProgramLicense = useMutation({ mutationFn: EditProgramLicense });

  const validateImageFile = (file) => {
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];

    if (!allowedTypes.includes(file.type)) return "FileType";

    return null;
  };

  const handleRemoveImageFile = () => {
    setNewFile(null);
    setFileError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleImageFile = async (selectedFile) => {
    if (!selectedFile) return;

    // The original is set at once so a save during the resize still carries a
    // picture; the scaled copy replaces it as soon as it is ready.
    const error = validateImageFile(selectedFile);
    setNewFile(selectedFile);
    setFileError(error);
    if (!error) setNewFile(await resizeImageForUpload(selectedFile));
  };

  const handleSaveNewNameOrImage = () => {
    if (fileError) return;

    const trimmedName = (newLicenseName ?? "").trim();

    // APL: the name field is disabled (LicenseName must stay unchanged); only the image is editable here.
    if (isAplProgram) {
      if (!newFile) return;
    } else if (trimmedName === licenseName && !newFile) {
      return;
    }

    const formData = new FormData();

    formData.append("Id", licenseId);
    // Send blank for APL so the backend preserves the existing LicenseName (EditProgram ignores blank).
    formData.append("LicenseName", isAplProgram ? "" : trimmedName || licenseName);
    if (newFile) {
      formData.append("ImageFile", newFile);
    }

    editProgramLicense.mutate(formData, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["FetchAdminCourses"] });
        toast.success("Licens uppdaterat");
        clearData();
      },
      onError: (err) => {
        toast.warn(formatServiceError(intl, err?.response?.data));
      },
    });
    toggle();
  };

  const clearData = () => {
    setFileError("");
    setNewFile(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  useEffect(() => {
    setNewLicenseName(licenseName);
  }, [licenseName]);

  return (
    <Modal isOpen={isOpen} toggle={() => toggle()}>
      <ModalBody>
        <CardTitle>
          <FormattedMessage id="Edit" />
        </CardTitle>
        <Card>
          <CardBody>
            <Label for="LicenseName">
              <FormattedMessage id="LicenseName" />:
            </Label>
            <Input
              name="LicensName"
              type="text"
              disabled={isAplProgram}
              onChange={(e) => setNewLicenseName(e.target.value)}
              value={isAplProgram ? programName ?? "" : newLicenseName ?? ""}
            ></Input>
            <Label for="LicenseImage">
              <FormattedMessage id="LicenseImage" />:
            </Label>
            <CardImg
              src={
                newFile ? URL.createObjectURL(newFile) : "img/" + licenseImage
              }
              alt="Card image cap"
              width="100%"
            />
            <div className="d-flex align-items-center">
              <Input
                name="Upload Image"
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={(e) => handleImageFile(e.target.files[0])}
                className="form-control mt-2 mb-2"
                innerRef={inputRef}
              ></Input>
              {newFile && (
                <span title={intl.formatMessage({ id: "RemoveImage" })}>
                  <FontAwesomeIcon
                    icon="trash"
                    className="ms-2 mb-2"
                    style={{
                      cursor: "pointer",
                      transition: "color 0.2s",
                      color: "black",
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.color = "red")}
                    onMouseOut={(e) => (e.currentTarget.style.color = "black")}
                    onClick={handleRemoveImageFile}
                  />
                </span>
              )}
            </div>
            {fileError === "FileType" && (
              <Col className="text-danger">
                <FormattedMessage id="FileType" />
              </Col>
            )}
          </CardBody>
        </Card>
      </ModalBody>
      <ModalFooter>
        <Button
          color="secondary"
          outline
          onClick={() => {
            clearData();
            toggle();
          }}
        >
          <FormattedMessage id="Cancel" />
        </Button>
        <Button
          color="primary"
          outline
          disabled={fileError}
          onClick={handleSaveNewNameOrImage}
        >
          <FormattedMessage id="Save" />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ModifyProgramLicenseModal;
